import React, { FC, useMemo } from "react";
import withModal from 'lib/helpers/hoc/withModal'
import CreatableSelect from "react-select/creatable";
import { useTranslation } from "react-i18next";
import { customStyles } from "lib/helpers/configs/customStyles";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import Label from "components/atoms/input/input-label/InputLabel";
import Input from "components/atoms/input/Input";
import TextArea from "components/atoms/text-area/TextArea";
import ToggleSwitch from "components/molecules/toggle-switch/ToggleSwitch";
import IconButton from "components/atoms/button/IconButton";
import Icon from "components/atoms/icon/Icon";
import { IconType } from "lib/helpers/constants/iconTypes";
import { useNewQualityQuestion } from "lib/hooks/mutations/Settings/useNewQualityQuestion";

interface CreateAndUpdateQuestionsProps {
    data?: any;
    onConfirm: () => void;
    isUpdate?: boolean;
}

const CreateAndUpdateQuestions: FC<CreateAndUpdateQuestionsProps> = ({ data, onConfirm, isUpdate = false }) => {

    const { t } = useTranslation();

    const {
        control,
        register,
        watch,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: {
            question_type: null,
            response_type: null,
            question_text: '',
            description: '',
            is_required: true,
            response_options: [{ option: '' }]
        }
    });

    const {
        fields,
        append,
        remove,
    } = useFieldArray<any>({
        control,
        name: "response_options",
    });

    const questionTypeOption = useMemo(() => {
        return [
            "Understanding & Clarity",
            "Satisfaction & Experience",
            "Decision & Next Steps",
            "Personal Considerations",
            "Follow-up & Support",
            "Documents Completion"
        ]

    }, [])

    const responseTypeOption = useMemo(() => {
        return [
            {
                "label": t("Multiple Choice"),
                "value": "multiple_choice"
            },
            {
                "label": t("Yes/No"),
                "value": "yes_no"
            },
            {
                "label": t("Short Text"),
                "value": "short_text"
            },
            {
                "label": t("Long Text"),
                "value": "long_text"
            },
            {
                "label": t("Dropdown Selection"),
                "value": "dropdown_selection"
            },
            {
                "label": t("Rating Scale 1 To 10"),
                "value": "rating_scale"
            }
        ]


    }, [t])

    const { mutateAsync, isLoading } = useNewQualityQuestion()


    const CreateNewQuestion = (data: any) => {
        let response_data;

        let finalData

        if (data.response_type === 'dropdown_selection' || data.response_type === 'multiple_choice') {
            response_data = data.response_options.map((item: any) => item.option);
        }

        else {
            response_data = null
        }

        finalData = {
            ...data,
            response_options: response_data
        }

        mutateAsync(finalData).then(() => {
            onConfirm()
        })


    }

    return (
        <form onSubmit={handleSubmit(CreateNewQuestion)} className="flex flex-col gap-7">
            <div className="grid grid-cols-2 gap-4">
                <div className="relative col-span-2 sm:col-span-1">
                    <Label>
                        {t('Question Type')}
                    </Label>
                    <Controller
                        control={control}
                        name={`question_type`}
                        rules={{ required: t('This field is required') }}
                        render={({ field }) => (
                            <CreatableSelect<any, false>
                                {...field}
                                placeholder={t('Choose Question Type')}
                                options={questionTypeOption.map((question: string) => (
                                    {
                                        value: question,
                                        label: t(question)
                                    }
                                ))}
                                value={
                                    field.value
                                        ? { value: field.value, label: field.value }
                                        : null
                                }
                                className="cursor-pointer !w-full font-inter-regular react-select-container"
                                classNamePrefix="react-select !w-full"
                                onChange={(newValue: any, actionMeta: any) => {
                                    if (actionMeta.action === "select-option" && newValue?.value) {
                                        field.onChange(newValue.value);
                                    }
                                }}
                                styles={customStyles}
                                isValidNewOption={() => false}
                                menuPosition={"fixed"}
                                menuPortalTarget={document.body}
                            />
                        )}

                    />
                    {errors?.question_type && (
                        <div className="font-inter-regular text-xs text-red-600 w-max absolute bottom-[-16px] 2xl:bottom-[-16px]  left-[2px]">
                            {errors?.question_type.message as any}
                        </div>
                    )}
                </div>
                <div className="relative col-span-2 sm:col-span-1">
                    <Label>
                        {t('Response Type')}
                    </Label>
                    <Controller
                        control={control}
                        name={`response_type`}
                        rules={{ required: t('This field is required') }}
                        render={({ field }) => (
                            <CreatableSelect<any, false>
                                {...field}
                                placeholder={t('Choose Response Type')}
                                options={responseTypeOption}
                                value={
                                    field.value
                                        ? { value: field.value, label: t(responseTypeOption.filter((response) => response.value === field.value)[0].label) }
                                        : null
                                }
                                className="cursor-pointer !w-full font-inter-regular react-select-container"
                                classNamePrefix="react-select !w-full"
                                onChange={(newValue: any, actionMeta: any) => {
                                    if (actionMeta.action === "select-option" && newValue?.value) {
                                        field.onChange(newValue.value);
                                    }
                                }}
                                styles={customStyles}
                                isValidNewOption={() => false}
                                menuPosition={"fixed"}
                                menuPortalTarget={document.body}
                            />
                        )}

                    />
                    {errors?.response_type && (
                        <div className="font-inter-regular text-xs text-red-600 w-max absolute bottom-[-16px] 2xl:bottom-[-16px]  left-[2px]">
                            {errors?.response_type.message as any}
                        </div>
                    )}
                </div>
                {(watch('response_type') === 'multiple_choice' || watch('response_type') === 'dropdown_selection') ? (
                    <div className="space-y-4 col-span-2">
                        {fields.map((field: any, index: number) => (
                            <div key={field.id}>
                                <Label>
                                    {t('Option')} {index + 1}
                                </Label>
                                <div className="flex items-center gap-3" key={field.id}>
                                    <Controller
                                        name={`response_options.${index}.option`}
                                        rules={{ required: t('This field is required') }}
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                classes={{ container: 'flex-1' }}
                                                placeholder={t('Enter option')}
                                                error={errors.response_options?.[index]?.option}
                                            />
                                        )}
                                    />
                                    <div className="cursor-pointer" onClick={() => { remove(field.id) }}>
                                        <Icon iconType={IconType.DELETE_ICON} />
                                    </div>
                                </div>
                            </div>
                        ))}

                        <div className="flex justify-end">
                            <div
                                className="flex items-center gap-2 cursor-pointer"
                                onClick={() => append({ option: '' })}
                            >
                                <Icon iconType={IconType.PLUS_ICON} color="var(--theme)" />
                                <span className="text-sm font-inter-medium text-theme">
                                    {t('New Option')}
                                </span>
                            </div>
                        </div>
                    </div>
                ) : null}
                <div className="col-span-2">
                    <Input
                        label="Question Text"
                        placeholder="Enter question text"
                        {...register('question_text', { required: 'This field is required' })}
                        error={errors?.question_text}
                    />
                </div>
                <div className="col-span-2">
                    <TextArea
                        label="Description"
                        placeholder="Write description.."
                        {...register('description')}
                        error={errors?.description}
                    />
                </div>
                <div className="col-span-2">
                    <div className="flex items-center gap-2">
                        <Label>{t('Required')} ?</Label>
                        <ToggleSwitch
                            {...register('is_required')}
                        />
                    </div>
                </div>
            </div>
            <div className="flex justify-end">
                <IconButton disabled={isLoading} icon={<Icon iconType={IconType.PLUS_ICON} />}>
                    Create Question
                </IconButton>
            </div>
        </form>
    )
}
export default withModal(CreateAndUpdateQuestions)
